import { Fragment, memo } from 'react';

import { useStopMeasureWhenRendered } from '@mint-lib/datadog';
import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import {
  Autocomplete,
  Box,
  CurrencyField,
  Icon,
  MenuItem,
  NumberField,
  PercentField,
  Popover,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@myn/mui';

import { BUILDER_ROW_TYPES } from '../../../../../../constants/campaignBuilder.js';
import { ValueType } from '../../../../../../types/_campaigns/campaignBuilder.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import { Goal, PreparedGoal, SavedGoal } from './types.js';
import useEditableGoalField from './useEditableGoalField.jsx';
import usePreparedGoalOptions from './usePreparedGoalOptions.js';

export type Props = {
  value: SavedGoal | null;
  column: BuilderTableColumn & { columnMeta: { [key: string]: Goal[] } };
  row: BuilderTableRow;
  cellColor: string;
};

const GoalField = memo(({ value, column, row, cellColor }: Props) => {
  const { t } = useTranslation('@myn/planning');

  const { currencyValue: currency, scale, type, parent } = row.original;

  const {
    isReadOnlyField,
    isRowLocked,
    selectedGoalOption,
    selectedGoalValue,
    goalValueDecimalLimit,
    popoverAnchorElement,
    handleChangeRowLock,
    handlePopoverOpen,
    handlePopoverClose,
    handleClearGoal,
    handleChangeGoalOption,
    handleChangeGoalValue,
    handleEnableEnterKeyDownSkip,
    handleDisableEnterKeyDownSkip,
  } = useEditableGoalField({
    value,
    column,
    row,
  });
  const isTileRow = type === BUILDER_ROW_TYPES.TILE;
  const isPopoverOpened = Boolean(popoverAnchorElement);

  const goalOptions = usePreparedGoalOptions({
    columnMeta: column.columnMeta,
    isTileRow,
    parentId: parent,
  });

  const preparedGoalVariantValue: PreparedGoal = {
    goalLabel: selectedGoalOption?.label ?? null,
    goalValueType: selectedGoalOption?.valueType ?? null,
    goalValue: selectedGoalValue,
    isLocked: isRowLocked,
  };

  const formattingFieldProps = {
    value: selectedGoalValue,
    onChange: handleChangeGoalValue,
    maxDecimals: goalValueDecimalLimit,
    restFormatProps: {
      min: 0,
      minDecimals: goalValueDecimalLimit,
    },
    fullWidth: true,
    placeholder: t('Insert values'),
  };

  const renderFormattingField = () => {
    if (!selectedGoalOption) {
      return null;
    }

    switch (selectedGoalOption.valueType) {
      case ValueType.DECIMAL: {
        if (currency) {
          return (
            <CurrencyField currency={currency} {...formattingFieldProps} />
          );
        }

        return <NumberField {...formattingFieldProps} />;
      }
      case ValueType.PERCENT: {
        return <PercentField {...formattingFieldProps} />;
      }
      case ValueType.INT:
      default: {
        return <NumberField {...formattingFieldProps} />;
      }
    }
  };

  useStopMeasureWhenRendered(
    TRACKING_EVENTS.VALUE_INSERTION,
    {
      react: { component: 'GoalField' },
    },
    { shouldIgnoreRender: isPopoverOpened },
  );

  if (!isTileRow) {
    return null;
  }

  if (isReadOnlyField) {
    return (
      <FormattedField
        scale={scale}
        color={cellColor}
        currency={currency}
        value={preparedGoalVariantValue}
        variant="goal"
      />
    );
  }

  return (
    <>
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={!!value && !isRowLocked}
        deleteValueFunc={handleClearGoal}
        showEditModeFunc={handlePopoverOpen}
        startMeasurePerformance={column.startMeasurePerformance}
        currency={currency}
        value={preparedGoalVariantValue}
        variant="goal"
        placeholder={t('Row Goal Not Set')}
      />
      <Popover
        open={isPopoverOpened}
        onClose={handlePopoverClose}
        anchorEl={popoverAnchorElement}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          <PopoverHeader>
            <SwitchContainer>
              <Switch
                size="small"
                checked={isRowLocked}
                onClick={handleChangeRowLock}
                data-testid="lock-toggle-button"
              />
              <Typography variant="bodyShort01">
                {isRowLocked ? t('Row Locked') : t('Row Unlocked')}
              </Typography>
            </SwitchContainer>
            <Tooltip
              title={t(
                'When the row is locked it will be excluded from any recommendations',
              )}
            >
              <IconWrapper>
                <Icon component="Information" />
              </IconWrapper>
            </Tooltip>
          </PopoverHeader>
          {!isRowLocked && (
            <PopoverBody>
              <Autocomplete
                size="medium"
                options={goalOptions}
                getOptionLabel={(option) => option.label}
                groupBy={(option) => option.groupLabel || ''}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t('Row Goal Not Set')} />
                )}
                renderGroup={(params) => (
                  <Fragment key={params.key}>
                    {params.group ? (
                      <GroupHeader disabled>{params.group}</GroupHeader>
                    ) : null}
                    <GroupItems as="ul">{params.children}</GroupItems>
                  </Fragment>
                )}
                value={selectedGoalOption}
                onChange={handleChangeGoalOption}
                onOpen={handleEnableEnterKeyDownSkip}
                onClose={handleDisableEnterKeyDownSkip}
              />
              {renderFormattingField()}
            </PopoverBody>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
});

export default GoalField;

const PopoverContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  width: 304px;
  padding: ${({ theme }) => theme.spacing(1.5)};
`;

const PopoverHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: ${({ theme }) => theme.spacing(3)};
`;

const SwitchContainer = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
`;

const IconWrapper = styled(Box)`
  width: 16px;
  height: 16px;
`;

const PopoverBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupHeader = styled(MenuItem)`
  padding: ${({ theme }) =>
    `${theme.spacing(2.25)} ${theme.spacing(1.5)} ${theme.spacing(1.25)}`};
  font-size: 11px;
  text-transform: uppercase;
`;

const GroupItems = styled(MenuItem)`
  padding: 0;
`;
